import React from 'react';
import {Box, styled} from '@mui/material';
import {Body, designSystemToken} from '@lightricks/react-design-system';
import {CollaborationGigBase} from '@/types/gigs';
import {
  formatFollowers,
  formatFeeDisplay,
  formatDeliverables,
  getFeeNoteText,
} from './utils';
import SocialNetworkIcon from '@/components/icons/SocialNetwork';

interface CollaborationGigFooterProps {
  gigData: CollaborationGigBase;
}

const StyledCardFooter = styled(Box)(() => ({
  display: 'flex',
  padding: '12px 0px',
  borderTop: `1px solid ${designSystemToken('semantic.bg.neutral-secondary')}`,
}));

const StyledFooterItem = styled(Box)(() => ({
  'flex': 1,
  'textAlign': 'center',
  'display': 'flex',
  'flexDirection': 'column',
  'justifyContent': 'flex-start',
  'alignItems': 'center',
  'height': '50px',
  '&:not(:last-child)': {
    'position': 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      right: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      width: '1px',
      height: '70%',
      backgroundColor: designSystemToken('semantic.bg.neutral-secondary'),
    },
  },
}));

const StyledFooterTitle = styled(Body)(() => ({
  fontWeight: 'bold',
  fontSize: '14px',
  color: designSystemToken('semantic.fg.primary'),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '4px',
  marginBottom: '2px',
}));

const StyledFooterNote = styled(Body)(() => ({
  fontSize: '12px',
  color: designSystemToken('semantic.fg.secondary'),
  textAlign: 'center',
}));

/**
 * CollaborationGigFooter component for displaying key statistics.
 * Shows followers count, fee information, and deliverables count.
 */
function CollaborationGigFooter({gigData}: CollaborationGigFooterProps) {
  const {
    unlocksAt,
    feeType,
    feeAmount,
    feeSuggestedMin,
    feeSuggestedMax,
    commissionRate,
    deliverableCounts,
  } = gigData;

  return (
    <StyledCardFooter>
      <StyledFooterItem>
        <StyledFooterTitle size="md">
          {gigData.network && <SocialNetworkIcon network={gigData.network} />}
          {formatFollowers(unlocksAt)}
        </StyledFooterTitle>
        <StyledFooterNote size="sm">Followers</StyledFooterNote>
      </StyledFooterItem>

      <StyledFooterItem>
        <StyledFooterTitle size="md">
          {formatFeeDisplay(
            feeType,
            feeAmount,
            feeSuggestedMin,
            feeSuggestedMax
          )}
        </StyledFooterTitle>
        <StyledFooterNote size="sm">
          {getFeeNoteText(
            feeType,
            commissionRate !== null && commissionRate !== undefined
          )}
        </StyledFooterNote>
      </StyledFooterItem>

      <StyledFooterItem>
        <StyledFooterTitle size="md">
          {formatDeliverables(deliverableCounts?.total)}
        </StyledFooterTitle>
        <StyledFooterNote size="sm">Deliverables</StyledFooterNote>
      </StyledFooterItem>
    </StyledCardFooter>
  );
}

export default CollaborationGigFooter;
